import { pushRoute } from '../../lib/router.js';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { Col, Grid, Row } from 'react-bootstrap';
import ProjectForm from '../../components/projects/ProjectForm.jsx';
import { getOrgTermsContent } from '../../content/utils.js';
import { mapFinalFormErrors, momentToDateString } from '../../lib/utils.js';
import {
  createProject,
  retrievePlanStatus,
  retrieveProject,
  retrieveProjects,
  selectProject,
  updateProject,
} from '../../modules/projects.js';
import ProjectFormHeader from '../../components/headers/PageHeaders/Project/ProjectFormHeader.jsx';
import {
  retrieveProjectOwners,
  selectProjectOwnersWithArchived,
} from '../../modules/projectOwners.js';
import SubHeader from '../../components/SubHeader.jsx';
import { searchUsers, selectSearchUsersResults } from '../../modules/users.js';
import {
  retrieveProjectStatuses,
  selectProjectStatusesWithArchived,
} from '../../modules/projectStatuses.js';
import {
  retrievePortfolios,
  selectPortfolios,
} from '../../modules/portfolio.js';
import ProjectsLimit from '../../components/projects/ProjectsLimit.jsx';
import useFormInitialValues from '../../hooks/useFormInitialValues.js';

const mapErrors = mapFinalFormErrors('Failed to save project');

const defaultValues = {
  startDate: null,
  endDate: null,
};

const mapStateToProps = (state, ownProps) =>
  createSelector(selectProject(ownProps.projectId), (project) => ({
    project,
    // Form is editing if projectId is provided
    editing: typeof ownProps.projectId !== 'undefined',
    portfolios: selectPortfolios(state),
    projectOwners: selectProjectOwnersWithArchived(state),
    customProjectStatuses: selectProjectStatusesWithArchived(state),
    searchResults: selectSearchUsersResults(state),
  }))(state);

const mapDispatchToProps = {
  createProject,
  retrieveProject,
  retrieveProjects,
  retrievePortfolios,
  updateProject,
  retrieveProjectOwners,
  retrievePlanStatus,
  searchUsers,
  retrieveProjectStatuses,
};

const ProjectFormContainer = (props) => {
  const {
    editing,
    portfolioId,
    projectId,
    project,
    portfolios,
    retrieveProject,
    retrievePortfolios,
    retrieveProjectOwners,
    createProject,
    updateProject,
    projectOwners,
    retrievePlanStatus,
    customProjectStatuses,
    retrieveProjectStatuses,
    retrieveProjects,
  } = props;
  const [selectedOwnerIds, updateSelectedOwnerIds] = useState([]);
  const [selectedPortfolio, updateSelectedPortfolio] = useState([]);
  const [planStatus, setPlanStatus] = useState(true);

  const filteredPortfolios = useMemo(
    () => portfolios?.filter((p) => !p.draft && p.id !== 'no_portfolio'),
    [portfolios]
  );
  const formResource = project
    ? { ...project, projectStatusId: `${project.projectStatusId}` }
    : null;

  const initialValues = useFormInitialValues(formResource, defaultValues);

  const onSubmit = (values) => {
    const resource = {
      name: values.name,
      ownerIds: selectedOwnerIds,
      startDate: momentToDateString(values.startDate),
      endDate: momentToDateString(values.endDate),
      scope: values.scope,
      projectStatusId: values.projectStatusId,
      impact: values.impact,
      archived: values.archived,
      portfolioId: selectedPortfolio.length ? selectedPortfolio[0].id : null,
    };

    const submitPromise = editing
      ? updateProject(projectId, resource)
      : createProject(resource);

    return submitPromise
      .then(({ result }) => {
        retrieveProjects().then(() =>
          pushRoute('projects/show', { projectId: result })
        );
      })
      .catch(mapErrors);
  };

  const validate = (values) => {
    const errors = {};

    if (!values.name) {
      errors.name = 'Title is required';
    }

    if (!selectedOwnerIds.length) {
      errors.projectOwner = 'Owner is required';
    }

    if (!values.startDate) {
      errors.startDate = 'Start Date is required';
    }

    if (!values.endDate) {
      errors.endDate = 'End Date is required';
    }

    if (!values.scope) {
      errors.scope = 'Scope is required';
    }

    if (!values.impact) {
      errors.impact = 'Impact is required';
    }

    if (!values.projectStatusId) {
      errors.projectStatusId = getOrgTermsContent('requiredProjectStatus');
    }

    return errors;
  };

  useEffect(() => {
    Promise.all([
      retrieveProjectOwners({ objectType: 'project', objectId: projectId }),
      retrievePortfolios(),
      retrieveProjectStatuses(),
    ]);

    if (!editing) {
      retrievePlanStatus().then(({ canCreateProject }) => {
        setPlanStatus(canCreateProject);
      });
    } else {
      retrieveProject(projectId);
    }
  }, [projectId, editing]);

  useEffect(() => {
    if (project?.owners) {
      updateSelectedOwnerIds(project.owners.map((owner) => owner.id));
    }
  }, [project]);

  useEffect(() => {
    if (project?.portfolioId) {
      updateSelectedPortfolio([{ id: project.portfolioId }]);
    } else if (portfolioId) {
      updateSelectedPortfolio([{ id: portfolioId }]);
    }
  }, [project?.portfolioId, portfolioId]);

  return !editing && !planStatus ? (
    <ProjectsLimit />
  ) : (
    <React.Fragment>
      <ProjectFormHeader project={project} />
      <SubHeader flexJustify small>
        <h2>
          {project
            ? `Edit ${getOrgTermsContent('project')}`
            : `Add ${getOrgTermsContent('project')}`}
        </h2>
      </SubHeader>
      {projectOwners ? (
        <Grid>
          <Row>
            <Col sm={12}>
              <div className="component-xl component-bg-white component-xl-padding-vertical component-no-horizontal-padding">
                <Row>
                  <Col sm={10} smOffset={1}>
                    <Form
                      onSubmit={onSubmit}
                      validate={validate}
                      initialValues={initialValues}
                      editing={editing}
                      projectStatuses={customProjectStatuses}
                      selectedOwnerIds={selectedOwnerIds}
                      updateSelectedOwnerIds={updateSelectedOwnerIds}
                      projectOwners={projectOwners}
                      planStatus={planStatus}
                      portfolios={filteredPortfolios}
                      selectedPortfolio={selectedPortfolio}
                      updateSelectedPortfolio={updateSelectedPortfolio}
                      isArchived={project?.archived}
                      component={ProjectForm}
                    />
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Grid>
      ) : null}
    </React.Fragment>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ProjectFormContainer);

ProjectFormContainer.propTypes = {
  projectId: PropTypes.number,
};
